import { CustomerServicePolicyPanel } from '../components/customer-services.js';
import { CustomerDatabasePolicyPanel } from '../components/customer-databases.js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateResourceInput,
  LimitDescriptorDto,
  LimitOverrides,
  LimitValue,
  PlanDescriptorsDto,
  PlanDto,
  PlanFeatures,
  PlanLimits,
  ResourceDto,
  ResourceKind,
  SubscriptionDto,
  SubscriptionStatus,
} from '@cpm/shared';
import { formatLimitAmount } from '@cpm/shared';
import {
  AppWindow,
  ArrowLeft,
  Boxes,
  Database,
  Plus,
  SlidersHorizontal,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client.js';
import { SubscriptionBackupsPanel } from '../components/backups-panels.js';
import { SubscriptionDiskPanel } from '../components/disk-panels.js';
import { DnsSummary } from '../components/dns-summary.js';
import { SubscriptionFilesPanel } from '../components/files-panels.js';
import { SubscriptionMailMigrationPanel } from '../components/mail-migration-panel.js';
import { SubscriptionMailPanel } from '../components/mail-panels.js';
import { SubscriptionMetricsPanel } from '../components/metrics-panels.js';
import { SubscriptionProtectionPanel } from '../components/subscription-protection-panel.js';
import { Modal } from '../components/Modal.js';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorNote,
  Input,
  Label,
  MeterBar,
  PageHeader,
  Select,
  Spinner,
  statusTone,
} from '../components/ui.js';

interface SubscriptionDetailResponse {
  subscription: SubscriptionDto;
  resources: ResourceDto[];
  /** Keyed by limit key; registry-driven (every usage provider reports). */
  usage: Record<string, number>;
  limits: PlanLimits;
  features: PlanFeatures;
}

/**
 * Per-subscription limit overrides (Plesk: "customize subscription").
 * Registry-driven: one row per limit descriptor; unchecked rows follow the
 * plan. Override keys without a descriptor (module uninstalled) are kept in
 * the payload untouched so saving never drops them.
 */
function OverridesForm({
  descriptors,
  planLimits,
  initial,
  onSubmit,
  pending,
  error,
}: {
  descriptors: LimitDescriptorDto[];
  planLimits: PlanLimits;
  initial: LimitOverrides | null;
  onSubmit: (overrides: LimitOverrides | null) => void;
  pending: boolean;
  error: unknown;
}) {
  const [overrides, setOverrides] = useState<Record<string, LimitValue>>({ ...initial });

  const setOverride = (key: string, value: LimitValue | undefined) => {
    setOverrides((current) => {
      const next = { ...current };
      if (value === undefined) delete next[key];
      else next[key] = value;
      return next;
    });
  };

  const known = new Set(descriptors.map((d) => d.key));
  const orphanedKeys = Object.keys(overrides).filter((key) => !known.has(key));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(Object.keys(overrides).length > 0 ? overrides : null);
      }}
      className="space-y-4"
    >
      <p className="text-sm text-slate-500 dark:text-slate-400">
        Overridden limits replace the plan value for this subscription only; everything else follows
        the plan.
      </p>
      <div className="space-y-2">
        {descriptors.map((descriptor) => {
          const overridden = descriptor.key in overrides;
          const value = overrides[descriptor.key] ?? null;
          const readOnly = !descriptor.active;
          return (
            <div
              key={descriptor.key}
              className="grid grid-cols-[1fr_auto] items-center gap-3 rounded-md border border-slate-100 px-3 py-2 text-sm dark:border-slate-800"
            >
              <div>
                <label className="flex items-center gap-2 font-medium" title={descriptor.hint}>
                  <input
                    type="checkbox"
                    className="size-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
                    checked={overridden}
                    disabled={readOnly}
                    onChange={(e) =>
                      setOverride(
                        descriptor.key,
                        e.target.checked ? (planLimits[descriptor.key] ?? null) : undefined,
                      )
                    }
                  />
                  {descriptor.label}
                  {readOnly && (
                    <span className="text-xs font-normal text-slate-400">(read-only)</span>
                  )}
                </label>
                <p className="ml-6 text-xs text-slate-500 dark:text-slate-400">
                  Plan: {formatLimitAmount(planLimits[descriptor.key], descriptor.kind)}
                </p>
              </div>
              {overridden && (
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={0}
                    className="w-28"
                    disabled={value === null || readOnly}
                    value={value === null ? '' : value}
                    placeholder={value === null ? 'Unlimited' : undefined}
                    onChange={(e) =>
                      setOverride(
                        descriptor.key,
                        e.target.value === '' ? 0 : Number(e.target.value),
                      )
                    }
                  />
                  <label className="flex shrink-0 items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
                    <input
                      type="checkbox"
                      className="size-3.5 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
                      checked={value === null}
                      disabled={readOnly}
                      onChange={(e) => setOverride(descriptor.key, e.target.checked ? null : 0)}
                    />
                    ∞
                  </label>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {orphanedKeys.length > 0 && (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Preserved overrides from unavailable modules:{' '}
          {orphanedKeys
            .map((key) => `${key}=${overrides[key] === null ? 'Unlimited' : overrides[key]}`)
            .join(', ')}
        </p>
      )}
      {error != null && <ErrorNote error={error} />}
      <div className="flex justify-end">
        <Button type="submit" loading={pending}>
          Save overrides
        </Button>
      </div>
    </form>
  );
}

function AddResourceForm({
  subscriptionId,
  onDone,
}: {
  subscriptionId: string;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<ResourceKind>('application');
  const [name, setName] = useState('');
  const [coolifyUuid, setCoolifyUuid] = useState('');

  const create = useMutation({
    mutationFn: (input: CreateResourceInput) =>
      api.post<ResourceDto>(`/api/subscriptions/${subscriptionId}/resources`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['subscriptions', subscriptionId],
      });
      onDone();
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate({ kind, name, coolifyUuid: coolifyUuid || null });
      }}
      className="space-y-4"
    >
      <div>
        <Label htmlFor="res-kind">Type</Label>
        <Select
          id="res-kind"
          value={kind}
          onChange={(e) => setKind(e.target.value as ResourceKind)}
        >
          <option value="application">Application</option>
          <option value="database">Database</option>
          <option value="service">Service</option>
        </Select>
      </div>
      <div>
        <Label htmlFor="res-name">Name</Label>
        <Input id="res-name" required value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div>
        <Label htmlFor="res-uuid">Coolify resource UUID (optional)</Label>
        <Input
          id="res-uuid"
          placeholder="Link to an existing Coolify resource"
          value={coolifyUuid}
          onChange={(e) => setCoolifyUuid(e.target.value)}
        />
      </div>
      {create.error != null && <ErrorNote error={create.error} />}
      <div className="flex justify-end">
        <Button type="submit" loading={create.isPending}>
          Add resource
        </Button>
      </div>
    </form>
  );
}

export function SubscriptionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [addOpen, setAddOpen] = useState(false);
  const [overridesOpen, setOverridesOpen] = useState(false);

  const detail = useQuery({
    queryKey: ['subscriptions', id],
    queryFn: () => api.get<SubscriptionDetailResponse>(`/api/subscriptions/${id}`),
    enabled: Boolean(id),
  });

  const plans = useQuery({
    queryKey: ['plans'],
    queryFn: () => api.get<PlanDto[]>('/api/plans/'),
  });

  const descriptors = useQuery({
    queryKey: ['plan-descriptors'],
    queryFn: () => api.get<PlanDescriptorsDto>('/api/plans/descriptors'),
  });

  const update = useMutation({
    mutationFn: (input: {
      status?: SubscriptionStatus;
      planId?: string;
      limitOverrides?: LimitOverrides | null;
    }) => api.patch<SubscriptionDto>(`/api/subscriptions/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      setOverridesOpen(false);
    },
  });

  const removeResource = useMutation({
    mutationFn: (resourceId: string) =>
      api.delete(`/api/subscriptions/${id}/resources/${resourceId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['subscriptions', id] });
    },
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`/api/subscriptions/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      void navigate('/subscriptions');
    },
  });

  if (detail.isPending) return <Spinner />;
  if (detail.error) return <ErrorNote error={detail.error} />;
  const { subscription, resources, usage, limits } = detail.data;

  return (
    <div>
      <Link
        to="/subscriptions"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
      >
        <ArrowLeft className="size-4" /> All subscriptions
      </Link>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            {subscription.name}{' '}
            <Badge tone={statusTone(subscription.status)}>{subscription.status}</Badge>
            <Badge tone={statusTone(subscription.health)}>{subscription.health}</Badge>
            {subscription.overQuota && <Badge tone="red">over quota</Badge>}
          </span>
        }
        description={
          <>
            Client:{' '}
            <Link
              to={`/clients/${subscription.clientId}`}
              className="text-brand-600 hover:underline dark:text-brand-400"
            >
              {subscription.clientName}
            </Link>
          </>
        }
        actions={
          <>
            {subscription.status === 'active' ? (
              <Button variant="secondary" onClick={() => update.mutate({ status: 'suspended' })}>
                Suspend
              </Button>
            ) : subscription.status === 'suspended' ? (
              <Button variant="secondary" onClick={() => update.mutate({ status: 'active' })}>
                Activate
              </Button>
            ) : null}
            {resources.length === 0 && (
              <Button
                variant="danger"
                loading={remove.isPending}
                onClick={() => {
                  if (
                    window.confirm(
                      'Delete this subscription? The Coolify project (if any) is kept and must be removed in Coolify.',
                    )
                  ) {
                    remove.mutate();
                  }
                }}
              >
                <Trash2 className="size-4" /> Delete
              </Button>
            )}
          </>
        }
      />
      {(update.error ?? remove.error) != null && (
        <div className="mb-4">
          <ErrorNote error={update.error ?? remove.error} />
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader title="Plan" />
          <div className="space-y-4 px-5 py-4 text-sm">
            <div>
              <Label htmlFor="sub-plan-switch">Service plan</Label>
              <Select
                id="sub-plan-switch"
                value={subscription.planId}
                onChange={(e) => update.mutate({ planId: e.target.value })}
              >
                {(
                  plans.data ?? [
                    {
                      id: subscription.planId,
                      name: subscription.planName,
                    } as PlanDto,
                  ]
                ).map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <p className="text-slate-500 dark:text-slate-400">Coolify project</p>
              <p className="font-medium">
                {subscription.coolifyProjectUuid ? (
                  <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs dark:bg-slate-800">
                    {subscription.coolifyProjectUuid}
                  </code>
                ) : (
                  'Not linked'
                )}
              </p>
            </div>
          </div>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader
            title="Usage & limits"
            description="Counted against the effective plan limits"
            actions={
              <Button variant="secondary" onClick={() => setOverridesOpen(true)}>
                <SlidersHorizontal className="size-4" /> Customize limits
              </Button>
            }
          />
          <div className="space-y-4 px-5 py-4">
            {(descriptors.data?.limits ?? [])
              .filter((descriptor) => descriptor.hasUsage)
              .map((descriptor) => (
                <div key={descriptor.key}>
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span>
                      {descriptor.label}
                      {subscription.limitOverrides?.[descriptor.key] !== undefined && (
                        <span className="ml-2">
                          <Badge tone="blue">override</Badge>
                        </span>
                      )}
                    </span>
                    <span className="tabular-nums text-slate-500 dark:text-slate-400">
                      {formatLimitAmount(usage[descriptor.key] ?? 0, descriptor.kind)} /{' '}
                      {formatLimitAmount(limits[descriptor.key], descriptor.kind)}
                    </span>
                  </div>
                  <MeterBar
                    value={usage[descriptor.key] ?? 0}
                    max={limits[descriptor.key] ?? null}
                  />
                </div>
              ))}
            <div className="grid grid-cols-2 gap-3 border-t border-slate-100 pt-3 text-sm dark:border-slate-800 sm:grid-cols-4">
              {(descriptors.data?.limits ?? [])
                .filter((descriptor) => !descriptor.hasUsage)
                .map((descriptor) => (
                  <div key={descriptor.key} title={descriptor.hint}>
                    <p className="text-slate-500 dark:text-slate-400">
                      {descriptor.label}
                      {subscription.limitOverrides?.[descriptor.key] !== undefined && (
                        <span className="ml-1.5">
                          <Badge tone="blue">override</Badge>
                        </span>
                      )}
                    </p>
                    <p className="font-medium">
                      {formatLimitAmount(limits[descriptor.key], descriptor.kind)}
                    </p>
                  </div>
                ))}
            </div>
          </div>
        </Card>
      </div>

      <div className="mt-4">
        <SubscriptionMetricsPanel subscriptionId={subscription.id} />
      </div>

      <div className="mt-4">
        <SubscriptionDiskPanel subscriptionId={subscription.id} />
      </div>

      <div className="mt-4">
        <SubscriptionBackupsPanel subscriptionId={subscription.id} />
      </div>

      <div className="mt-4">
        <SubscriptionProtectionPanel subscriptionId={subscription.id} />
      </div>

      <div className="mt-4">
        <SubscriptionMailPanel subscriptionId={subscription.id} />
      </div>

      <div className="mt-4">
        <SubscriptionMailMigrationPanel subscriptionId={subscription.id} />
      </div>

      <div className="mt-4">
        <DnsSummary subscriptionId={subscription.id} />
      </div>

      <div className="mt-4">
        <CustomerDatabasePolicyPanel subscriptionId={subscription.id} />
      </div>
      <div className="mt-4">
        <CustomerServicePolicyPanel subscriptionId={subscription.id} />
      </div>

      <div className="mt-4">
        <SubscriptionFilesPanel subscriptionId={subscription.id} />
      </div>

      <div className="mt-4">
        <Card>
          <CardHeader
            title="Resources"
            description="What this subscription owns in Coolify"
            actions={
              <>
                {(
                  [
                    {
                      label: 'New application',
                      path: 'applications/new',
                      icon: AppWindow,
                    },
                    {
                      label: 'New database',
                      path: 'databases/new',
                      icon: Database,
                    },
                    { label: 'New service', path: 'services/new', icon: Boxes },
                  ] as const
                ).map(({ label, path, icon: Icon }) => (
                  <Button
                    key={path}
                    onClick={() => void navigate(`/subscriptions/${subscription.id}/${path}`)}
                    disabled={!subscription.coolifyProjectUuid || subscription.status !== 'active'}
                    title={
                      !subscription.coolifyProjectUuid
                        ? 'No Coolify project linked'
                        : subscription.status !== 'active'
                          ? 'Subscription is not active'
                          : undefined
                    }
                  >
                    <Icon className="size-4" /> {label}
                  </Button>
                ))}
                <Button variant="secondary" onClick={() => setAddOpen(true)}>
                  <Plus className="size-4" /> Track resource
                </Button>
              </>
            }
          />
          {resources.length === 0 ? (
            <EmptyState
              icon={<Boxes className="size-10" />}
              title="No resources yet"
              description="Track the applications, databases and services this subscription owns. Limits are enforced when resources are added."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
                  <tr>
                    <th className="px-5 py-3 font-medium">Name</th>
                    <th className="px-5 py-3 font-medium">Type</th>
                    <th className="px-5 py-3 font-medium">Coolify UUID</th>
                    <th className="px-5 py-3 font-medium">Status</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {resources.map((resource) => (
                    <tr key={resource.id}>
                      <td className="px-5 py-3 font-medium">
                        <Link
                          to={`/${resource.kind}s/${resource.id}`}
                          className="text-brand-600 hover:underline dark:text-brand-400"
                        >
                          {resource.name}
                        </Link>
                      </td>
                      <td className="px-5 py-3 capitalize text-slate-600 dark:text-slate-300">
                        {resource.kind}
                      </td>
                      <td className="px-5 py-3">
                        {resource.coolifyUuid ? (
                          <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs dark:bg-slate-800">
                            {resource.coolifyUuid}
                          </code>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <span className="flex flex-wrap gap-1.5">
                          <Badge tone={statusTone(resource.status)}>
                            {resource.health
                              ? `${resource.status}:${resource.health}`
                              : resource.status}
                          </Badge>
                          {resource.orphanedAt && <Badge tone="red">orphaned</Badge>}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <button
                          className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10"
                          onClick={() => removeResource.mutate(resource.id)}
                          aria-label={`Remove ${resource.name}`}
                        >
                          <Trash2 className="size-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <Modal title="Add resource" open={addOpen} onClose={() => setAddOpen(false)}>
        <AddResourceForm subscriptionId={subscription.id} onDone={() => setAddOpen(false)} />
      </Modal>

      <Modal
        title="Customize limits"
        open={overridesOpen}
        onClose={() => setOverridesOpen(false)}
        wide
      >
        {descriptors.data && overridesOpen && (
          <OverridesForm
            descriptors={descriptors.data.limits}
            planLimits={plans.data?.find((p) => p.id === subscription.planId)?.limits ?? {}}
            initial={subscription.limitOverrides}
            onSubmit={(limitOverrides) => update.mutate({ limitOverrides })}
            pending={update.isPending}
            error={update.error}
          />
        )}
      </Modal>
    </div>
  );
}
